// Phase 0b PR 11: TanStack Query bindings for FieldLineagePopover +
// OpsAuditTimeline. All RPCs are view-only — no mutations, so cache
// invalidation isn't required.
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { projectDataClient } from '../../../../lib/connect/clients';
export function useFieldLineage({ targetObjectType, targetObjectId, targetFieldPath, enabled = true }) {
    return useQuery({
        queryKey: [
            'field-lineage',
            targetObjectType,
            targetObjectId,
            targetFieldPath
        ],
        enabled: enabled && !!targetObjectId && !!targetFieldPath,
        queryFn: async ()=>{
            const resp = await projectDataClient.getFieldLineage({
                targetObjectType,
                targetObjectId,
                targetFieldPath
            });
            return resp;
        }
    });
}
// Extraction lands source_reference rows well after the CLIN/PWS sync that
// makes an object visible in the UI (~40s behind it on a fresh program, per
// SAD-339) — a query fired the moment a drawer mounts can genuinely race
// that and see an empty result. pollWhileEmpty keeps re-checking every 5s
// until a ref shows up, then stops; callers on data that's expected to be
// immediately complete (SourceChip's existing CLIN targets) leave it off.
const POLL_WHILE_EMPTY_INTERVAL_MS = 5000;
// The ~40s population window above is the reason to poll at all; it is not a
// reason to poll forever. A target with no provenance row is a normal,
// permanent state (e.g. an optional task whose PWS section legitimately has
// no source refs), so bound the attempts well past that window — 24 attempts
// at 5s is 2 minutes, ~3x the documented delay — and stop outright once
// TanStack's own retry policy has given up on a hard error.
const POLL_WHILE_EMPTY_MAX_ATTEMPTS = 24;
function pollWhileEmptyInterval(query) {
    if ((query.state.data?.length ?? 0) > 0) return false;
    if (query.state.error) return false;
    if (query.state.dataUpdateCount + query.state.errorUpdateCount >= POLL_WHILE_EMPTY_MAX_ATTEMPTS) {
        return false;
    }
    return POLL_WHILE_EMPTY_INTERVAL_MS;
}
// useSourceRefsForTarget fetches all persisted source-provenance rows for one
// object (e.g. a CLIN by clin_number) in a single query — multiple SourceChips
// on the same object share the cache and filter by field path client-side.
export function useSourceRefsForTarget({ targetObjectType, targetObjectId, enabled = true, pollWhileEmpty = false }) {
    return useQuery({
        queryKey: [
            'source-refs',
            targetObjectType,
            targetObjectId
        ],
        enabled: enabled && !!targetObjectType && !!targetObjectId,
        staleTime: 60000,
        refetchInterval: pollWhileEmpty ? pollWhileEmptyInterval : undefined,
        queryFn: async ()=>{
            const resp = await projectDataClient.listSourceRefsForTarget({
                targetObjectType,
                targetObjectId,
                currentOnly: true
            });
            return resp.refs;
        }
    });
}
// useSourceRefsForPrefix fetches EVERY ref of a type whose target id starts
// with the prefix — one query for a whole program (e.g. "{programId}/" for
// its PwsObligation refs) instead of one RPC per chip. Consumers pick their
// rows client-side via ref.targetObjectId.
export function useSourceRefsForPrefix({ targetObjectType, targetObjectIdPrefix, enabled = true, pollWhileEmpty = false }) {
    return useQuery({
        queryKey: [
            'source-refs-prefix',
            targetObjectType,
            targetObjectIdPrefix
        ],
        enabled: enabled && !!targetObjectType && !!targetObjectIdPrefix,
        staleTime: 60000,
        refetchInterval: pollWhileEmpty ? pollWhileEmptyInterval : undefined,
        queryFn: async ()=>{
            const resp = await projectDataClient.listSourceRefsForTarget({
                targetObjectType,
                targetObjectIdPrefix,
                currentOnly: true
            });
            return resp.refs;
        }
    });
}
export function useOpsAuditEvents(filters) {
    return useQuery({
        queryKey: [
            'ops-audit-events',
            filters
        ],
        // Paging swaps the whole result set; without this the table and its
        // pager unmount into the loading state on every page step.
        placeholderData: keepPreviousData,
        queryFn: async ()=>{
            const resp = await projectDataClient.listOpsAuditEvents({
                actionKeyPrefix: filters.actionKeyPrefix ?? '',
                actionKeyPrefixes: filters.actionKeyPrefixes ?? [],
                objectType: filters.objectType ?? '',
                objectId: filters.objectId ?? '',
                actorUserId: filters.actorUserId ?? '',
                correlationId: filters.correlationId ?? '',
                limit: filters.limit ?? 0,
                offset: filters.offset ?? 0
            });
            return resp;
        }
    });
}
// programId narrows the thread to one program's rows, which is the only lane
// open to a caller without tenant-wide audit authority - pass the program
// whose timeline the thread was opened from.
//
// Hands back the whole response rather than just events: the program lane caps
// its query server-side and answers with total + truncated, and a thread that
// silently drops its earliest steps is exactly what an audit reader must not be
// shown. Those two fields only reach the panel if they survive the hook.
export function useOpsAuditByCorrelation(correlationId, programId) {
    return useQuery({
        queryKey: [
            'ops-audit-correlation',
            correlationId,
            programId ?? ''
        ],
        enabled: !!correlationId,
        queryFn: async ()=>{
            const resp = await projectDataClient.getOpsAuditByCorrelation({
                correlationId: correlationId ?? '',
                programId: programId ?? ''
            });
            return resp;
        }
    });
}
